<script setup lang="ts">
import AppContainer from '@apparatix/library/AppContainer.vue';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Column from 'primevue/column';
import FileExportIcon from '@primeicons/vue/file-export';
import GaugeIcon from '@primeicons/vue/gauge';
import HistoryIcon from '@primeicons/vue/history';
import InfoCircleIcon from '@primeicons/vue/info-circle';
import RefreshIcon from '@primeicons/vue/refresh';
import DatePicker from 'primevue/datepicker';
import DataTable, { type DataTableFilterEvent, type DataTablePageEvent, type DataTableRowClickEvent, type DataTableSortEvent } from 'primevue/datatable';
import FileUpload, { type FileUploadSelectEvent } from 'primevue/fileupload';
import HeightOptimizer from '@apparatix/library/HeightOptimizer.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';
import MultiSelect from '@apparatix/library/MultiSelect.vue';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import UserRoleGate from '@apparatix/library/UserRoleGate.vue';
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { FilterMatchMode } from '@primevue/core/api';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';
import { Section } from '@apparatix/library/core';
import Typography from '@apparatix/library/core/typography/Typography.ts';
import { useEndpoint } from '@apparatix/composables';
import {
  HISTORY_FIELDS,
  useMeasurementsStore,
  type MeasurementRow,
  type ProviderPriorityRow,
  type SelectOption,
} from '@apparatix/stores/useMeasurementsStore';

const measurementsStore = useMeasurementsStore();
const {
  measurements,
  totalRecords,
  providerOptions,
  metricOptions,
  segmentOptions,
  intervalOptions,
  venueOptions,
  adminToolsEnabled,
  selectedProvider,
  selectedMetric,
  selectedSegment,
  selectedInterval,
  rowsPerPage,
  firstRecord,
  tableSortField,
  tableSortOrder,
  tableFilters,
  showHistory,
  historySince,
  providerPriority,
  originalProviderOrder,
} = storeToRefs(measurementsStore);

const router = useRouter();
const route = useRoute();

const onRowClick = (event: DataTableRowClickEvent) => {
  const row = event.data as { inv_id: number; inv_number: string };
  const base = (route.meta?.basePath as string) ?? '/measurements';

  router.push({
    path: `${base}/detail/inventory/${row.inv_id}/measurement`,
    query: { invNumber: row.inv_number },
  });
};

const file = ref<File | null>(null);
const message = ref('');
const error = ref('');
const warning = ref('');

const interval = ref('28');
const headerRow = ref(1);
const firstDataRow = ref(2);
const downloadPreUpdateImpressions = ref(true);

// Upload-scoped provider/audience (separate from the table selectors). Defaults
// match the legacy inv_dec behavior (DEC provider, Persons 0+).
const uploadProvider = ref('DEC');
const uploadSegment = ref('P0+');

// Interval the uploaded impressions are reported over (days the figure covers).
const uploadIntervalOptions = [
  { name: '4 Week', value: '28' },
  { name: 'Weekly', value: '7' },
  { name: 'Daily', value: '1' },
];

const impressionsIntervalTooltip = 'Have the impressions been provided as 4-week impressions, weekly impressions, or daily impressions?';
const headerRowTooltip = 'The row containing the headers: Faces, Impressions.';
const firstDataRowTooltip = 'The row containing the first face/impression data.';
const downloadPreUpdateImpressionsTooltip = 'Downloads face impressions from before the update, expressed at the selected interval. To revert an update, upload this file with the same Provider, Audience, and Interval.';

const filterDescriptions = {
  provider:
    'Which data source supplies the value. "Default (calculated)" walks every provider for the selected audience, in priority order (1st, 2nd, 3rd…) and uses the highest-ranked source that has a non-zero value for each data point. If none have a value, it falls back to DEC Persons 0+. Pick a specific provider to see only its values.',
  metric:
    'The kind of measurement shown — e.g. Impressions (eyes on), Reach, Frequency, TRP. Daily Effective Circulation is Impressions supplied by the DEC provider. Reach, Frequency, TRP and Reach % are non-additive and only display at the interval they were measured at.',
  segment:
    'The audience the measurement is counted for (e.g. Persons 0+, 18+, 21+). Defaults to your configured impressions audience.',
  interval:
    'The time window the value is expressed over: Daily, Weekly, or 4 Week. Additive metrics (Impressions, Plays) are scaled from the stored weekly value to the selected interval. Non-additive metrics (Reach, Frequency, TRP, Reach %) can\'t be scaled — they show a value only for faces whose data is stored at the selected interval, and are blank otherwise (including 4 Week, which isn\'t stored).',
};

type PreUpdateImpression = {
  face: string;
  impressions: string | number | null;
};

type UploadMeasurementsResponse = {
  success: boolean;
  message?: string;
  warning?: string;
  validCount?: number;
  invalidCount?: number;
  invalidFaces?: string[];
  skippedRowCount?: number;
  skippedRows?: unknown[];
  preUpdateImpressions?: PreUpdateImpression[];
};

type UploadMeasurementsError = {
  error?: string;
  message?: string;
  errors?: Record<string, string[]>;
};

const formatUploadError = (response?: UploadMeasurementsError) => {
  if (response?.error) {
    return response.error;
  }

  if (response?.errors) {
    return Object.values(response.errors).flat().join('\n');
  }

  if (response?.message) {
    return response.message;
  }

  return 'Upload failed.';
};

const { getResults: uploadMeasurements, isLoading: isUploading } = useEndpoint<UploadMeasurementsResponse, UploadMeasurementsError>({
  type: 'POST',
  url: '/manage-measurements/upload',
  successField: 'success',
  onError: response => {
    error.value = formatUploadError(response);
  },
  toast: { showOnSuccess: false, showOnError: false },
});

const handleFileSelect = (event: FileUploadSelectEvent) => {
  file.value = (event.files as File[])[0] ?? null;
  message.value = '';
  error.value = '';
  warning.value = '';
};

const escapeCsvValue = (value: string | number | null | undefined) => {
  const stringValue = value === null || value === undefined ? '' : String(value);

  if (!(/[",\n\r]/).test(stringValue)) {
    return stringValue;
  }

  return `"${stringValue.replace(/"/g, '""')}"`;
};

const downloadPreUpdateImpressionsCsv = (rows: PreUpdateImpression[]) => {
  if (rows.length === 0) {
    return;
  }

  const csv = [
    ['Faces', 'Impressions'],
    ...rows.map(row => [row.face, row.impressions]),
  ]
    .map(row => row.map(escapeCsvValue).join(','))
    .join('\n');

  const blob = new Blob([`${csv}\n`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `pre-update-impressions-${filenameTimestamp()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const filenameTimestamp = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');

  return [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
  ].join('-') + `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const uploadFile = async () => {
  message.value = '';
  error.value = '';
  warning.value = '';
  if (!file.value || isUploading.value) {
    return;
  }

  const formData = new FormData();
  formData.append('file', file.value);
  formData.append('interval', interval.value);
  formData.append('headerRow', String(headerRow.value));
  formData.append('firstDataRow', String(firstDataRow.value));
  formData.append('downloadPreUpdateImpressions', downloadPreUpdateImpressions.value ? '1' : '0');
  formData.append('provider', uploadProvider.value);
  formData.append('segment', uploadSegment.value);

  const response = await uploadMeasurements<UploadMeasurementsResponse, FormData>(formData);

  if (!response) {
    return;
  }

  message.value = response.message ?? '';
  warning.value = response.warning ?? '';

  if (downloadPreUpdateImpressions.value) {
    await nextTick();
    downloadPreUpdateImpressionsCsv(response.preUpdateImpressions ?? []);
  }

  file.value = null;
};

//
// Measurements data table (server-side paginated)
//

type MeasurementsTableResponse = {
  data?: {
    measurements?: MeasurementRow[];
    permissions?: { tag: string }[];
  };
  pagination?: {
    total?: number;
    perPage?: number;
    currentPage?: number;
  };
};

type MeasurementsOptionsResponse = {
  data?: {
    providers?: SelectOption[];
    metrics?: SelectOption[];
    segments?: SelectOption[];
    venues?: SelectOption[];
    intervals?: SelectOption[];
    defaults?: { provider?: string; metric?: string; segment?: string; interval?: string };
    permissions?: { tag: string }[];
  };
};

const dt = ref();
const isTableLoading = ref(false);

// Uploader provider list excludes the synthetic "default" (calculated) option —
// data can only be imported under a real provider.
const uploadProviderOptions = computed(() => providerOptions.value.filter(provider => provider.code !== 'default'));

const isHistoryAvailable = computed(() => selectedProvider.value !== 'default');

const historyButtonTooltip = computed(() => {
  if (!isHistoryAvailable.value) {
    return 'History is only available for a specific provider. Select a provider other than Default (calculated) to view previous values.';
  }

  return showHistory.value
    ? 'Hide the previous value, change, date changed, and changed by columns.'
    : 'Show the previous value, how much it changed, when it changed, and who changed it.';
});

const previousValueHeader = computed(() => {
  const metricName = metricOptions.value.find(m => m.code === selectedMetric.value)?.name ?? 'Value';
  const providerName = providerOptions.value.find(p => p.code === selectedProvider.value)?.name ?? '';
  const prefix = historySince.value ? `${metricName}` : `Previous ${metricName}`;
  const suffix = historySince.value ? ` on ${historySince.value}` : '';
  return `${prefix} — ${selectedSegment.value} — ${providerName}${suffix}`.trim();
});

const changeHeader = computed(() => (historySince.value ? `Change Since ${historySince.value}` : 'Change'));

const historyCompareTooltip = 'Most recent change compares each value with the one it replaced. Pick a date to instead compare each value with the one that was in effect on that date; picking a date turns off most recent change, and turning most recent change back on clears the date.';

// Y-m-d in local time (toISOString would shift the day across the UTC boundary).
const toYmd = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const historySinceDate = computed(() => {
  if (!historySince.value) {
    return null;
  }

  const [year, month, day] = historySince.value.split('-').map(Number);
  return new Date(year, month - 1, day);
});

const today = new Date();

const valueHeader = computed(() => {
  const metricName = metricOptions.value.find(m => m.code === selectedMetric.value)?.name ?? 'Value';
  const intervalName = intervalOptions.value.find(i => i.code === selectedInterval.value)?.name ?? '';
  const suffix = selectedProvider.value === 'default'
    ? '(Default)'
    : (providerOptions.value.find(p => p.code === selectedProvider.value)?.name ?? '');
  return `${metricName} — ${selectedSegment.value} — ${intervalName} ${suffix}`.trim();
});

// Render the value cell. A null/undefined value (no data, or a non-additive metric
// with nothing stored at the selected interval) shows blank rather than "0".
const formatValue = (value: number | null | undefined) =>
  value === null || value === undefined
    ? ''
    : Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 });

// Signed change from the previous value: "+" for a gain, "-" for a loss.
const formatChange = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }

  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 4, signDisplay: 'exceptZero' });
};

// A row has a current value but nothing before it in the provider's history (the
// first value recorded for this provider / metric / audience). Rows without a
// current value stay blank, matching the value column.
const hasNoPreviousDataPoint = (row: MeasurementRow) =>
  row.value !== null && row.value !== undefined
  && (row.previous_value === null || row.previous_value === undefined);

const noPreviousDataPointTooltip = computed(() => (historySince.value
  ? `No value was in effect on ${historySince.value} for this provider, metric, and audience, so there is nothing to compare against.`
  : 'This is the first recorded value for this provider, metric, and audience, so there is no earlier value to compare against.'));

const changeClass = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number(value) === 0) {
    return '';
  }

  return Number(value) > 0 ? 'text-green-600' : 'text-red-600';
};

// inv_number / changed_by are text searches (Contains default, Starts With);
// inv_id, value, previous_value and value_change are numeric searches (Contains,
// Less Than, Greater Than — value_change defaults to Greater Than, so 0 finds
// gains and Less Than 0 finds losses); date_changed is a date search (Date Is default, Before,
// After, Between).
const TEXT_MATCH_MODES = [
  { label: 'Contains', value: 'contains' },
  { label: 'Starts With', value: 'startsWith' },
];

const NUMERIC_MATCH_MODES = [
  { label: 'Contains', value: 'contains' },
  { label: 'Less Than', value: 'lt' },
  { label: 'Greater Than', value: 'gt' },
];

const DATE_MATCH_MODES = [
  { label: 'Date Is', value: FilterMatchMode.DATE_IS },
  { label: 'Before', value: FilterMatchMode.DATE_BEFORE },
  { label: 'After', value: FilterMatchMode.DATE_AFTER },
  { label: 'Between', value: FilterMatchMode.BETWEEN },
];

const matchModeOptionsFor = (field: string) => {
  if (field === 'inv_number' || field === 'changed_by') {
    return TEXT_MATCH_MODES;
  }

  return field === 'date_changed' ? DATE_MATCH_MODES : NUMERIC_MATCH_MODES;
};

type FilterModel = { value: unknown };

const dateFilterValue = (filterModel: FilterModel): string | null =>
  (typeof filterModel.value === 'string' ? filterModel.value : null);

const dateBetweenValue = (filterModel: FilterModel, index: 0 | 1): string | null => {
  const bounds = filterModel.value;
  const bound = Array.isArray(bounds) ? bounds[index] : null;
  return typeof bound === 'string' ? bound : null;
};

const onDateFilterInput = (filterModel: FilterModel, value: string | null | undefined, filterCallback: () => void) => {
  filterModel.value = value || null;
  filterCallback();
};

const onDateBetweenFilterInput = (
  filterModel: FilterModel,
  index: 0 | 1,
  value: string | null | undefined,
  filterCallback: () => void,
) => {
  const bounds = Array.isArray(filterModel.value) ? [...filterModel.value] : [null, null];
  bounds[index] = value || null;
  filterModel.value = bounds;
  filterCallback();
};

// Switching the date filter between Between (a [from, to] pair) and a single-date
// mode leaves a value of the wrong shape behind, so reset it when the mode changes.
watch(
  () => tableFilters.value.date_changed?.matchMode,
  (current, previous) => {
    if (previous && current !== previous && tableFilters.value.date_changed) {
      tableFilters.value.date_changed.value = null;
    }
  },
);

// Sentinel for "no resolved value" in the source-provider filter (mirrors the backend).
const EMPTY_PROVIDER = '__empty__';

// Source-provider filter options: each real provider by name, plus "(Empty)".
const providerFilterOptions = computed(() => [
  { label: '(Empty)', value: EMPTY_PROVIDER },
  ...providerOptions.value
    .filter(provider => provider.code !== 'default')
    .map(provider => ({ label: provider.name, value: provider.name })),
]);

const rowsPerPageOptions = computed(() => {
  const base = [100, 250, 500, 1000];
  const options = totalRecords.value > base[base.length - 1]
    ? [...base, totalRecords.value]
    : base;
  return [...new Set(options)].sort((a, b) => a - b);
});

const { getResults: fetchOptions } = useEndpoint<MeasurementsOptionsResponse>({
  type: 'GET',
  url: '/manage-measurements/options',
  successField: 'data',
  toast: { showOnSuccess: false },
});

const { getResults: fetchMeasurements } = useEndpoint<MeasurementsTableResponse>({
  type: 'POST',
  url: '/manage-measurements/table',
  successField: 'data',
  cancelPreviousRequestOnSubsequent: true,
  toast: { showOnSuccess: false },
});

// applyDefaults is true only on the first load; on a manual refresh the option
// lists are reloaded but the user's current selection is preserved.
const loadOptions = async (applyDefaults = false) => {
  const response = await fetchOptions();

  if (!response?.data) {
    return;
  }

  providerOptions.value = response.data.providers ?? [];
  metricOptions.value = response.data.metrics ?? [];
  segmentOptions.value = response.data.segments ?? [];
  venueOptions.value = response.data.venues ?? [];
  intervalOptions.value = response.data.intervals ?? [];
  adminToolsEnabled.value = (response.data.permissions ?? []).some(permission => permission.tag === 'hasAdminTools');

  if (applyDefaults) {
    selectedProvider.value = response.data.defaults?.provider ?? selectedProvider.value;
    selectedMetric.value = response.data.defaults?.metric ?? selectedMetric.value;
    selectedSegment.value = response.data.defaults?.segment ?? selectedSegment.value;
    selectedInterval.value = response.data.defaults?.interval ?? selectedInterval.value;
  }

  // Keep the uploader defaults (DEC / P0+) valid; fall back to the first available
  // option when the preferred default isn't present so the selects are never empty.
  if (!uploadProviderOptions.value.some(provider => provider.code === uploadProvider.value)) {
    uploadProvider.value = uploadProviderOptions.value[0]?.code ?? uploadProvider.value;
  }
  if (!segmentOptions.value.some(segment => segment.code === uploadSegment.value)) {
    uploadSegment.value = segmentOptions.value[0]?.code ?? uploadSegment.value;
  }
};

const loadMeasurements = async (page: number) => {
  isTableLoading.value = true;

  const response = await fetchMeasurements({
    page,
    pageSize: rowsPerPage.value,
    sortField: tableSortField.value,
    sortOrder: tableSortOrder.value,
    filters: tableFilters.value,
    provider: selectedProvider.value,
    metric: selectedMetric.value,
    segment: selectedSegment.value,
    interval: selectedInterval.value,
    includeHistory: showHistory.value && isHistoryAvailable.value,
    historySince: showHistory.value && isHistoryAvailable.value ? historySince.value : null,
  });

  if (response) {
    measurements.value = response.data?.measurements ?? [];
    totalRecords.value = response.pagination?.total ?? 0;
    adminToolsEnabled.value = (response.data?.permissions ?? []).some(permission => permission.tag === 'hasAdminTools');
  }

  isTableLoading.value = false;
};

// Derive the 1-based page from the persisted paginator offset so the table
// resumes on the same page after a navigation round-trip.
const currentPage = () => Math.floor(firstRecord.value / rowsPerPage.value) + 1;

// Clear the history columns' filters (and a sort on one of them) so hidden
// columns never silently constrain or order the table.
const resetHistoryTableState = () => {
  historySince.value = null;

  HISTORY_FIELDS.forEach(field => {
    if (tableFilters.value[field]) {
      tableFilters.value[field].value = null;
    }
  });

  if (tableSortField.value && HISTORY_FIELDS.includes(tableSortField.value)) {
    tableSortField.value = null;
    tableSortOrder.value = null;
  }
};

// Picking a date switches off "most recent change"; clearing it switches it back on.
const onHistorySinceChange = (value: Date | Date[] | (Date | null)[] | null | undefined) => {
  const nextValue = value instanceof Date ? toYmd(value) : null;

  if (nextValue === historySince.value) {
    return;
  }

  historySince.value = nextValue;
  firstRecord.value = 0;
  loadMeasurements(1);
};

// Turning "most recent change" on clears the date. It can't be turned off on its
// own — picking a date is what switches it off.
const onMostRecentToggle = (value: boolean) => {
  if (value && historySince.value) {
    onHistorySinceChange(null);
  }
};

const onProfileChange = () => {
  if (!isHistoryAvailable.value && showHistory.value) {
    showHistory.value = false;
    resetHistoryTableState();
  }

  firstRecord.value = 0;
  loadMeasurements(1);
};

const toggleHistory = () => {
  if (!isHistoryAvailable.value) {
    return;
  }

  showHistory.value = !showHistory.value;

  if (!showHistory.value) {
    resetHistoryTableState();
  }

  firstRecord.value = 0;
  loadMeasurements(1);
};

const onPage = (event: DataTablePageEvent) => {
  rowsPerPage.value = event.rows;
  firstRecord.value = event.first;
  loadMeasurements(currentPage());
};

const onSort = (event: DataTableSortEvent) => {
  tableSortField.value = (event.sortField as string) ?? null;
  tableSortOrder.value = event.sortOrder ?? null;
  firstRecord.value = 0;
  loadMeasurements(1);
};

const FILTER_DEBOUNCE_MS = 400;
let filterDebounceTimer: number | undefined;

const onFilter = (event: DataTableFilterEvent) => {
  tableFilters.value = { ...(event.filters as typeof tableFilters.value) };
  firstRecord.value = 0;

  if (filterDebounceTimer) {
    window.clearTimeout(filterDebounceTimer);
  }

  filterDebounceTimer = window.setTimeout(() => loadMeasurements(1), FILTER_DEBOUNCE_MS);
};

const exportCsv = () => {
  dt.value.exportCSV();
};

//
// Admin: provider priority ordering
//

type ProviderPriorityResponse = {
  data?: { providers?: ProviderPriorityRow[] };
};

type LegacyDecAudienceResponse = {
  data?: {
    audience?: string;
    audiences?: SelectOption[];
  };
};

const legacyDecAudience = ref('P0+');
const legacyDecAudienceOptions = ref<SelectOption[]>([]);


const { getResults: fetchProviderPriority, isLoading: isLoadingPriority } = useEndpoint<ProviderPriorityResponse>({
  type: 'GET',
  url: '/manage-measurements/providers',
  successField: 'data',
  toast: { showOnSuccess: false },
});

const { getResults: saveProviderPriorityRequest, isLoading: isSavingPriority } = useEndpoint<ProviderPriorityResponse>({
  type: 'POST',
  url: '/manage-measurements/providers/priority',
  successField: 'data',
  toast: { showOnSuccess: true, successMessage: 'Provider priority saved' },
});

const { getResults: fetchLegacyDecAudience, isLoading: isLoadingLegacyDecAudience } = useEndpoint<LegacyDecAudienceResponse>({
  type: 'GET',
  url: '/manage-measurements/legacy-dec-audience',
  successField: 'data',
  toast: { showOnSuccess: false },
});

const { getResults: saveLegacyDecAudienceRequest, isLoading: isSavingLegacyDecAudience } = useEndpoint<LegacyDecAudienceResponse>({
  type: 'POST',
  url: '/manage-measurements/legacy-dec-audience',
  successField: 'data',
  toast: { showOnSuccess: true, successMessage: 'Legacy DEC audience saved' },
});

const applyProviderPriority = (rows: ProviderPriorityRow[]) => {
  providerPriority.value = rows;
  originalProviderOrder.value = rows.map(row => row.id);
};

const providerOrderDirty = computed(() =>
  providerPriority.value.some((row, index) => row.id !== originalProviderOrder.value[index]),
);

const loadProviderPriority = async () => {
  const response = await fetchProviderPriority();

  if (response?.data?.providers) {
    applyProviderPriority(response.data.providers);
  }
};

const loadLegacyDecAudience = async () => {
  const response = await fetchLegacyDecAudience();

  if (!response?.data) {
    return;
  }

  legacyDecAudience.value = response.data.audience ?? legacyDecAudience.value;
  legacyDecAudienceOptions.value = response.data.audiences ?? [];
};

const saveLegacyDecAudience = async () => {
  if (isSavingLegacyDecAudience.value) {
    return;
  }

  const response = await saveLegacyDecAudienceRequest<LegacyDecAudienceResponse, { audience: string }>({
    audience: legacyDecAudience.value,
  });

  if (response?.data?.audience) {
    legacyDecAudience.value = response.data.audience;
  }
};

const onProviderReorder = (event: { value: ProviderPriorityRow[] }) => {
  providerPriority.value = event.value;
};

const saveProviderPriority = async () => {
  if (!providerOrderDirty.value || isSavingPriority.value) {
    return;
  }

  const response = await saveProviderPriorityRequest<ProviderPriorityResponse, { providers: number[] }>({
    providers: providerPriority.value.map(row => row.id),
  });

  if (response?.data?.providers) {
    applyProviderPriority(response.data.providers);
  }
};

// Re-pull data on demand, keeping the current selection / filters / sort / page.
const refresh = async () => {
  await loadOptions(false);
  await loadMeasurements(currentPage());

  if (adminToolsEnabled.value) {
    await loadProviderPriority();
    await loadLegacyDecAudience();
  }
};

onMounted(async () => {
  // Re-clicking the active "Manage Measurements" sidebar item dispatches this
  // event (see SidebarItemList.vue); registered before the initialized guard so
  // it stays wired up when returning to an already-loaded screen.
  window.addEventListener(CustomEventNames.measurements.reloadHome, refresh);

  // State lives in the Pinia store and survives this component being unmounted
  // when the user navigates away, so only fetch on the first visit. Use the
  // Refresh button to re-pull on demand.
  if (measurementsStore.initialized) {
    if (adminToolsEnabled.value) {
      await loadLegacyDecAudience();
    }

    return;
  }

  await loadOptions(true);
  await loadMeasurements(currentPage());

  if (adminToolsEnabled.value) {
    await loadProviderPriority();
    await loadLegacyDecAudience();
  }

  measurementsStore.initialized = true;
});

onBeforeUnmount(() => window.removeEventListener(CustomEventNames.measurements.reloadHome, refresh));

</script>

<template>
  <AppContainer>
    <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="flex items-center gap-2">
        <Avatar
          size="normal"
          shape="circle"
          style="background-color: var(--p-blue-100); color: var(--p-blue-500);"
        >
          <template #icon>
            <GaugeIcon />
          </template>
        </Avatar>
        <h1 class="text-lg">
          Manage Measurements
        </h1>
        <Badge :value="totalRecords" size="large" severity="secondary" />
      </div>
      <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
        <Button
          v-tooltip.left="'Refresh'"
          severity="secondary"
          class="shrink-0 whitespace-nowrap"
          :loading="isTableLoading"
          @click="refresh"
        >
          <RefreshIcon />
          <span class="font-bold">Refresh</span>
        </Button>
        <Button
          v-tooltip.left="'Export'"
          severity="secondary"
          class="shrink-0 whitespace-nowrap"
          @click="exportCsv()"
        >
          <FileExportIcon />
          <span class="font-bold">Export</span>
        </Button>
        <span v-tooltip.left="historyButtonTooltip" class="shrink-0">
          <Button
            class="w-full whitespace-nowrap"
            :class="{ 'pointer-events-none': !isHistoryAvailable }"
            :disabled="!isHistoryAvailable"
            :aria-pressed="showHistory"
            @click="toggleHistory"
          >
            <HistoryIcon />
            <span class="font-bold">{{ showHistory ? 'Hide History' : 'Show History' }}</span>
          </Button>
        </span>
      </div>
    </div>

    <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Provider
          <InfoCircleIcon v-tooltip="filterDescriptions.provider" color="var(--tw-text-gray-500)" class="cursor-pointer" />
        </label>
        <Select
          v-model="selectedProvider"
          :options="providerOptions"
          option-label="label"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Metric
          <InfoCircleIcon v-tooltip="filterDescriptions.metric" color="var(--tw-text-gray-500)" class="cursor-pointer" />
        </label>
        <Select
          v-model="selectedMetric"
          :options="metricOptions"
          option-label="name"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Audience
          <InfoCircleIcon v-tooltip="filterDescriptions.segment" color="var(--tw-text-gray-500)" class="cursor-pointer" />
        </label>
        <Select
          v-model="selectedSegment"
          :options="segmentOptions"
          option-label="name"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Interval
          <InfoCircleIcon v-tooltip="filterDescriptions.interval" color="var(--tw-text-gray-500)" class="cursor-pointer" />
        </label>
        <Select
          v-model="selectedInterval"
          :options="intervalOptions"
          option-label="name"
          option-value="code"
          class="w-60"
          @change="onProfileChange"
        />
      </div>
      <div v-if="showHistory" class="flex flex-col gap-1">
        <label class="flex items-center gap-1 text-sm font-bold">
          Compare To
          <InfoCircleIcon v-tooltip="historyCompareTooltip" color="var(--tw-text-gray-500)" class="cursor-pointer" />
        </label>
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex items-center gap-2">
            <ToggleSwitch
              input-id="historyMostRecent"
              :model-value="historySince === null"
              @update:model-value="onMostRecentToggle"
            />
            <label for="historyMostRecent" class="whitespace-nowrap text-sm">Most recent change</label>
          </div>
          <DatePicker
            :model-value="historySinceDate"
            date-format="yy-mm-dd"
            placeholder="See change since date"
            :max-date="today"
            show-icon
            show-button-bar
            class="w-60"
            @update:model-value="onHistorySinceChange"
          />
        </div>
      </div>
    </div>

    <HeightOptimizer>
      <template #default="{ availableHeight }">
        <DataTable
          ref="dt"
          v-model:filters="tableFilters"
          lazy
          :value="measurements"
          :loading="isTableLoading"
          :total-records="totalRecords"
          :rows="rowsPerPage"
          :first="firstRecord"
          paginator
          :rows-per-page-options="rowsPerPageOptions"
          filter-display="row"
          data-key="inv_id"
          table-style="min-width: 50rem;"
          table-layout="fixed"
          scrollable
          :scroll-height="`calc(${availableHeight} - 60px)`"
          :virtual-scroller-options="{ itemSize: 50 }"
          reorderable-columns
          resizable-columns
          column-resize-mode="expand"
          row-hover
          :pt="{ bodyRow: { class: 'cursor-pointer' } }"
          @page="onPage"
          @sort="onSort"
          @filter="onFilter"
          @row-click="onRowClick"
        >
          <Column
            field="inv_number"
            header="Number"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('inv_number')"
          >
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="inv_type"
            header="Type"
            sortable
            :show-filter-menu="false"
          >
            <template #body="{ data }">
              {{ venueOptions.find(v => v.code === data.inv_type)?.name ?? data.inv_type ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <MultiSelect
                v-model="filterModel.value"
                :options="venueOptions"
                option-label="name"
                option-value="code"
                placeholder="All"
                show-clear
                class="p-column-filter"
                style="min-width: 10rem"
                @change="filterCallback()"
              />
            </template>
          </Column>
          <Column
            field="value"
            :header="valueHeader"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('value')"
          >
            <template #body="{ data }">
              {{ formatValue(data.value) }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
          <Column
            v-if="selectedProvider === 'default'"
            field="value_provider"
            header="Source Provider"
            sortable
            :show-filter-menu="false"
          >
            <template #body="{ data }">
              {{ data.value_provider ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <Select
                v-model="filterModel.value"
                :options="providerFilterOptions"
                option-label="label"
                option-value="value"
                placeholder="All"
                show-clear
                class="p-column-filter"
                style="min-width: 10rem"
                @change="filterCallback()"
              />
            </template>
          </Column>
          <Column
            v-if="showHistory"
            field="previous_value"
            :header="previousValueHeader"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('previous_value')"
          >
            <template #body="{ data }">
              <span
                v-if="hasNoPreviousDataPoint(data)"
                v-tooltip.top="noPreviousDataPointTooltip"
                class="italic text-gray-500"
              >No previous data</span>
              <template v-else>
                {{ formatValue(data.previous_value) }}
              </template>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
          <Column
            v-if="showHistory"
            field="value_change"
            :header="changeHeader"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('value_change')"
          >
            <template #body="{ data }">
              <span
                v-if="hasNoPreviousDataPoint(data)"
                v-tooltip.top="noPreviousDataPointTooltip"
                class="italic text-gray-500"
              >No previous data</span>
              <span v-else :class="changeClass(data.value_change)">{{ formatChange(data.value_change) }}</span>
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
          <Column
            v-if="showHistory"
            field="date_changed"
            header="Date Set"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('date_changed')"
          >
            <template #body="{ data }">
              {{ data.date_changed ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <div v-if="filterModel.matchMode === FilterMatchMode.BETWEEN" class="flex gap-1">
                <InputText
                  type="date"
                  :model-value="dateBetweenValue(filterModel, 0)"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  @update:model-value="value => onDateBetweenFilterInput(filterModel, 0, value, filterCallback)"
                />
                <InputText
                  type="date"
                  :model-value="dateBetweenValue(filterModel, 1)"
                  class="p-column-filter"
                  style="min-width: 8rem; max-width: 8rem"
                  @update:model-value="value => onDateBetweenFilterInput(filterModel, 1, value, filterCallback)"
                />
              </div>
              <InputText
                v-else
                type="date"
                :model-value="dateFilterValue(filterModel)"
                class="p-column-filter"
                style="min-width: 8rem; max-width: 10rem"
                @update:model-value="value => onDateFilterInput(filterModel, value, filterCallback)"
              />
            </template>
          </Column>
          <Column
            v-if="showHistory"
            field="changed_by"
            header="Set By"
            sortable
            :filter-match-mode-options="matchModeOptionsFor('changed_by')"
          >
            <template #body="{ data }">
              {{ data.changed_by ?? '' }}
            </template>
            <template #filter="{ filterModel, filterCallback }">
              <InputText
                v-model="filterModel.value"
                type="text"
                class="p-column-filter"
                placeholder="Search"
                style="min-width: 6rem; max-width: 10rem"
                @input="filterCallback()"
              />
            </template>
          </Column>
        </DataTable>
      </template>
    </HeightOptimizer>

    <div v-if="adminToolsEnabled" class="pt-10">
      <Section :footer="false">
        <Typography class="pb-2" variant="section-heading">
          Admin Tools
        </Typography>

        <UserRoleGate :allow-for="['Technologist']">
          <div class="mb-4 max-w-4xl rounded-lg border p-3">
            <Typography class="pb-2" variant="section-subheading">
              Upload Impressions
            </Typography>
            <Typography class="pb-3" variant="form-label">
              Upload impressions via a csv, xls, or xlsx file with two columns: Face, Impressions.
              Face names must match the names as seen in CORE &gt; Inventory &gt; Faces in the REF column. Please remove any prefixes before attempting to upload.
              Choose the Provider, Audience, and Interval the impressions are reported for. The data is imported as a manual measurement owned by you.
            </Typography>

            <form @submit.prevent="uploadFile">
              <div class="mb-3">
                <Typography variant="form-label" for="file" class="mb-1 flex items-center gap-1">
                  Select File
                </Typography>
                <FileUpload
                  mode="basic"
                  accept=".csv,.xls,.xlsx"
                  choose-label="Choose File"
                  custom-upload
                  @select="handleFileSelect"
                />
              </div>

              <div class="grid gap-3 md:grid-cols-2">
                <div>
                  <Typography variant="form-label" class="mb-1 flex items-center gap-1">
                    Metric
                  </Typography>
                  <InputText
                    model-value="Impressions"
                    readonly
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="uploadProvider" class="mb-1 flex items-center gap-1">
                    Provider
                  </Typography>
                  <Select
                    id="uploadProvider"
                    v-model="uploadProvider"
                    :options="uploadProviderOptions"
                    option-label="name"
                    option-value="code"
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="uploadSegment" class="mb-1 flex items-center gap-1">
                    Audience
                  </Typography>
                  <Select
                    id="uploadSegment"
                    v-model="uploadSegment"
                    :options="segmentOptions"
                    option-label="name"
                    option-value="code"
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="interval" class="mb-1 flex items-center gap-1">
                    Impressions Interval
                    <InfoCircleIcon v-tooltip="impressionsIntervalTooltip" color="var(--tw-text-gray-500)" class="cursor-pointer" />
                  </Typography>
                  <Select
                    id="interval"
                    v-model="interval"
                    :options="uploadIntervalOptions"
                    option-label="name"
                    option-value="value"
                    class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="headerRow" class="mb-1 flex items-center gap-1">
                    Header Row
                    <InfoCircleIcon v-tooltip="headerRowTooltip" color="var(--tw-text-gray-500)" class="cursor-pointer" />
                  </Typography>
                  <InputNumber
                    v-model="headerRow"
                    input-id="headerRow"
                    :min="1"
                    :max-fraction-digits="0"
                    :use-grouping="false"
                    :allow-empty="false"
                    class="w-full"
                    input-class="w-full"
                  />
                </div>

                <div>
                  <Typography variant="form-label" for="firstDataRow" class="mb-1 flex items-center gap-1">
                    First Data Row
                    <InfoCircleIcon v-tooltip="firstDataRowTooltip" color="var(--tw-text-gray-500)" class="cursor-pointer" />
                  </Typography>
                  <InputNumber
                    v-model="firstDataRow"
                    input-id="firstDataRow"
                    :min="2"
                    :max-fraction-digits="0"
                    :use-grouping="false"
                    :allow-empty="false"
                    class="w-full"
                    input-class="w-full"
                  />
                </div>
              </div>

              <div class="mt-3 flex items-center gap-2">
                <Checkbox
                  v-model="downloadPreUpdateImpressions"
                  input-id="downloadPreUpdateImpressions"
                  binary
                />
                <Typography variant="form-label" for="downloadPreUpdateImpressions" class="flex items-center gap-1">
                  Download Pre-Update Impressions
                  <InfoCircleIcon v-tooltip="downloadPreUpdateImpressionsTooltip" color="var(--tw-text-gray-500)" class="cursor-pointer" />
                </Typography>
              </div>

              <div class="flex flex-wrap items-center gap-4 pt-3">
                <Button
                  size="small"
                  type="submit"
                  class="flex items-center"
                  :disabled="!file || isUploading"
                  :loading="isUploading"
                >
                  <span class="font-bold">Upload File</span>
                </Button>
                <a
                  href="/docs/templates/upload-impressions-template.csv"
                  class="text-[var(--text-link-primary)] hover:underline"
                >
                  Download Template CSV
                </a>
                <a
                  href="/docs/templates/upload-impressions-template.xlsx"
                  class="text-[var(--text-link-primary)] hover:underline"
                >
                  Download Template XLSX
                </a>
              </div>

              <p v-if="message" class="mt-4 whitespace-pre-line text-green-600">
                {{ message }}
              </p>
              <p v-if="error" class="mt-4 whitespace-pre-line text-red-600">
                {{ error }}
              </p>
              <p v-if="warning" class="mt-4 whitespace-pre-line text-yellow-600">
                {{ warning }}
              </p>
            </form>
          </div>
        </UserRoleGate>

        <div class="mb-4 max-w-2xl rounded-lg border p-3">
          <Typography class="pb-2" variant="section-subheading">
            Legacy DEC Audience
          </Typography>
          <Typography class="pb-3" variant="form-label">
            Set the audience that will be used for legacy DEC values (this will be the fallback value). Some older proposal,
            availability, reporting, pacing, agreement, and Master Schedule screens still use the legacy DEC value when no measured
            audience value is available. This setting controls which DEC audience they use. It does not change measurement data,
            provider priority, or the calculated table above. Use Persons 0+ unless the site intentionally maintains DEC for Persons
            18+ or Persons 21+.
          </Typography>

          <div class="flex flex-wrap items-end gap-3">
            <div class="flex flex-col gap-1">
              <Select
                id="legacyDecAudience"
                v-model="legacyDecAudience"
                :options="legacyDecAudienceOptions"
                option-label="name"
                option-value="code"
                :loading="isLoadingLegacyDecAudience"
                class="w-60"
              />
            </div>
            <Button
              size="small"
              :disabled="isLoadingLegacyDecAudience || isSavingLegacyDecAudience"
              :loading="isSavingLegacyDecAudience"
              @click="saveLegacyDecAudience"
            >
              <span class="font-bold">Save Legacy DEC Audience</span>
            </Button>
          </div>
        </div>

        <div class="mb-4 max-w-2xl rounded-lg border p-3">
          <Typography class="pb-2" variant="section-subheading">
            Provider Priority
          </Typography>
          <Typography class="pb-3" variant="form-label">
            Drag providers to set the read-resolution order. When a value is shown for the "Default (calculated)" provider,
            each data point uses the highest-ranked provider (top of this list) that has a non-zero value.
            Saving renumbers priorities to clean multiples of 100, leaving room to inject a provider between two ranks later.
          </Typography>

          <DataTable
            :value="providerPriority"
            :loading="isLoadingPriority"
            data-key="id"
            size="small"
            class="text-sm"
            @row-reorder="onProviderReorder"
          >
            <Column row-reorder header-style="width: 3rem" />
            <Column header="Rank" header-style="width: 4rem">
              <template #body="{ index }">
                {{ index + 1 }}
              </template>
            </Column>
            <Column field="name" header="Provider">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <span class="font-medium">{{ data.name }}</span>
                  <Tag v-if="!data.isActive" value="Inactive" severity="secondary" />
                </div>
              </template>
            </Column>
            <Column field="code" header="Code" />
          </DataTable>

          <div class="flex items-center gap-3 pt-3">
            <Button
              size="small"
              :disabled="!providerOrderDirty || isSavingPriority"
              :loading="isSavingPriority"
              @click="saveProviderPriority"
            >
              <span class="font-bold">Save Priority</span>
            </Button>
            <span v-if="providerOrderDirty" class="text-sm text-yellow-600">Unsaved changes</span>
          </div>
        </div>
      </Section>
    </div>
    <RouterView />
  </AppContainer>
</template>

<style scoped>
.datatable-container {
  /* Ensure the container occupies full height */
  height: 100vh;
  display: flex;
  flex-direction: column;
}

:deep(.p-datatable-header) {
    background: none;
}

.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

.p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
